import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { Copy, KeyRound, Loader2, Pencil, Plus, Printer as PrinterIcon, RotateCw, Trash2, TriangleAlert } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { FiscalAccessDenied } from '@/components/fiscal/FiscalAccessDenied';
import { formatRelativeTime } from '@/components/fiscal/fiscalFormat';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useAuth } from '@/hooks/useAuth';
import { hasCompanyRole } from '@/lib/permissions';
import { useFiscalApi, type CreateFiscalPrinterDto, type UpdateFiscalPrinterDto } from '@/services/api';
import { t } from '@/i18n';
import type { FiscalPrinterDto } from '@ip/shared-types';

const POLL_MS = 30_000;
const CODE_RE = /^[a-z0-9-]{1,32}$/;
const HHMM_RE = /^\d{2}:\d{2}$/;
const CENTRAL_URL = 'https://fv.fssrke3.com/api';
const DAILY_REPORT_MODES = ['central', 'agent'] as const;

type DeviceStatus = { status: string; name?: string; version?: string; error?: string } | null;

function deviceStatusOf(printer: FiscalPrinterDto): DeviceStatus {
  return (printer.deviceStatus as DeviceStatus) ?? null;
}

/** Overdue when the last successful daily report is older than yesterday
 *  (Warsaw-day prefix compare — lastDailyReport is either full ISO or the
 *  agent's 'YYYY-MM-DD HH:MM:SS' heartbeat format, both start with the date). */
function isDailyReportOverdue(lastDailyReport: string | null): boolean {
  if (!lastDailyReport) return false;
  const day = lastDailyReport.slice(0, 10);
  return day < dayjs().subtract(1, 'day').format('YYYY-MM-DD');
}

function buildDockerSnippet(code: string, token: string): string {
  return [
    'docker run -d \\',
    `  --name ksef-agent-${code} \\`,
    `  -e CENTRAL_URL=${CENTRAL_URL} \\`,
    `  -e AGENT_TOKEN=${token} \\`,
    '  -e PRINTER_URL=http://<IP-drukarki>:8888/api/v1 \\',
    `  -e PRINTER_ID=${code} \\`,
    '  -v ksef-agent-state:/state \\',
    '  --restart unless-stopped \\',
    '  gitlab.fss.cc:5050/fss-utils/ksef-agent:latest',
  ].join('\n');
}

interface PrinterFormValues {
  code: string;
  name: string;
  dailyReportMode: 'central' | 'agent';
  dailyReportTime: string;
  active: boolean;
}

const EMPTY_FORM: PrinterFormValues = {
  code: '',
  name: '',
  dailyReportMode: 'central',
  dailyReportTime: '23:55',
  active: true,
};

async function copyToClipboard(value: string, successKey: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(t(successKey));
  } catch {
    toast.error(t('common.error'));
  }
}

/**
 * Printer/agent fleet: status cards (online dot, device status, VAT rates,
 * queue/memory, daily-report freshness) with 30 s auto-refresh, plus
 * admin-only CRUD + one-time token reveal (create/regenerate) carrying a
 * ready `docker run` snippet for the on-prem agent. Read-only for
 * non-admins (ksiegowy/user) — mirrors the backend's checkCompanyRole gate
 * on every printer mutation.
 */
export function FiscalPrinters() {
  const { nip } = useOutletContext<AppShellContext>();
  const { isSuperAdmin, permissions } = useAuth();
  const isAdmin = isSuperAdmin || hasCompanyRole(permissions, nip, ['admin']);
  const { listPrinters, createPrinter, updatePrinter, regeneratePrinterToken, deletePrinter } = useFiscalApi();

  const [printers, setPrinters] = useState<FiscalPrinterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const loadSeq = useRef(0);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<PrinterFormValues>(EMPTY_FORM);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editing, setEditing] = useState<FiscalPrinterDto | null>(null);
  const [editForm, setEditForm] = useState<PrinterFormValues>(EMPTY_FORM);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [regenTarget, setRegenTarget] = useState<FiscalPrinterDto | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<FiscalPrinterDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [reveal, setReveal] = useState<{ code: string; token: string } | null>(null);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listPrinters(nip);
      if (seq !== loadSeq.current) return;
      setPrinters(result);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('fiscal.printers.loadFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, listPrinters]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const codeError = addForm.code && !CODE_RE.test(addForm.code) ? t('fiscal.printers.codeInvalid') : null;

  const handleAddSubmit = async () => {
    if (!CODE_RE.test(addForm.code)) {
      toast.error(t('fiscal.printers.codeInvalid'));
      return;
    }
    if (!addForm.name.trim()) {
      toast.error(t('fiscal.printers.nameRequired'));
      return;
    }
    if (!HHMM_RE.test(addForm.dailyReportTime)) {
      toast.error(t('fiscal.printers.timeInvalid'));
      return;
    }
    setAddSubmitting(true);
    try {
      const dto: CreateFiscalPrinterDto = {
        code: addForm.code,
        name: addForm.name.trim(),
        dailyReportMode: addForm.dailyReportMode,
        dailyReportTime: addForm.dailyReportTime,
      };
      const { printer, token } = await createPrinter(nip, dto);
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      await load();
      toast.success(t('fiscal.printers.createSuccess'));
      setReveal({ code: printer.code, token });
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.printers.createFailed'));
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEdit = (printer: FiscalPrinterDto) => {
    setEditing(printer);
    setEditForm({
      code: printer.code,
      name: printer.name,
      dailyReportMode: printer.dailyReportMode,
      dailyReportTime: printer.dailyReportTime,
      active: printer.active,
    });
  };

  const handleEditSubmit = async () => {
    if (!editing) return;
    if (!editForm.name.trim()) {
      toast.error(t('fiscal.printers.nameRequired'));
      return;
    }
    if (!HHMM_RE.test(editForm.dailyReportTime)) {
      toast.error(t('fiscal.printers.timeInvalid'));
      return;
    }
    setEditSubmitting(true);
    try {
      const dto: UpdateFiscalPrinterDto = {
        name: editForm.name.trim(),
        dailyReportMode: editForm.dailyReportMode,
        dailyReportTime: editForm.dailyReportTime,
        active: editForm.active,
      };
      await updatePrinter(nip, editing.id, dto);
      setEditing(null);
      await load();
      toast.success(t('fiscal.printers.updateSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.printers.updateFailed'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!regenTarget) return;
    const code = regenTarget.code;
    setRegenerating(true);
    try {
      const { token } = await regeneratePrinterToken(nip, regenTarget.id);
      setRegenTarget(null);
      toast.success(t('fiscal.printers.regenerateSuccess'));
      setReveal({ code, token });
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.printers.regenerateFailed'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePrinter(nip, deleteTarget.id);
      setDeleteTarget(null);
      await load();
      toast.success(t('fiscal.printers.deleteSuccess'));
    } catch (error) {
      // Surfaces the backend message verbatim — e.g. the 409 "pending
      // receipts" conflict tells the user exactly why the delete was refused.
      toast.error(error instanceof Error && error.message ? error.message : t('fiscal.printers.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (forbidden) return <FiscalAccessDenied />;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" onClick={load} aria-label={t('fiscal.printers.refresh')}>
          <RotateCw className="size-4" />
        </Button>
        {isAdmin && (
          <Button
            size="sm"
            onClick={() => {
              setAddForm(EMPTY_FORM);
              setAddOpen(true);
            }}
          >
            <Plus className="size-4" />
            {t('fiscal.printers.add')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-72 w-full rounded-xl" />
          ))}
        </div>
      ) : printers.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <PrinterIcon className="text-muted-foreground mx-auto mb-2 size-8" />
            <p className="font-medium">{t('fiscal.printers.empty.title')}</p>
            <p className="text-muted-foreground text-sm">{t('fiscal.printers.empty.description')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {printers.map((printer) => {
            const device = deviceStatusOf(printer);
            const overdue = isDailyReportOverdue(printer.lastDailyReport);
            const rates = printer.taxRates ? Object.entries(printer.taxRates) : [];
            return (
              <Card key={printer.id} className="gap-3 py-4">
                <CardHeader className="px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block size-2.5 shrink-0 rounded-full ${
                            printer.online ? 'bg-emerald-500' : 'bg-gray-400'
                          }`}
                          title={printer.online ? t('fiscal.printers.online') : t('fiscal.printers.offline')}
                        />
                        <span className="truncate font-medium">{printer.name}</span>
                      </div>
                      <p className="text-muted-foreground font-mono text-xs">{printer.code}</p>
                    </div>
                    {isAdmin && (
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('fiscal.printers.edit')}
                          onClick={() => openEdit(printer)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('fiscal.printers.regenerateToken')}
                          onClick={() => setRegenTarget(printer)}
                        >
                          <KeyRound className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive"
                          aria-label={t('fiscal.printers.delete')}
                          onClick={() => setDeleteTarget(printer)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {!printer.active && <Badge variant="secondary">{t('fiscal.printers.inactive')}</Badge>}
                    {printer.blackout && (
                      <Badge variant="outline" className="border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400">
                        {t('fiscal.printers.blackout')}
                      </Badge>
                    )}
                    {overdue && (
                      <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
                        {t('fiscal.printers.dailyReportOverdue')}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2.5 px-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('fiscal.printers.deviceStatus')}</span>
                    {device ? (
                      <Badge
                        variant="outline"
                        className={
                          device.status === 'OK'
                            ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                            : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400'
                        }
                      >
                        {device.status}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">{t('fiscal.printers.deviceStatusUnknown')}</span>
                    )}
                  </div>
                  {device?.error && <p className="text-destructive text-xs">{device.error}</p>}

                  <div>
                    <p className="text-muted-foreground mb-1 text-xs">{t('fiscal.printers.taxRatesTitle')}</p>
                    {rates.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {rates.map(([letter, pct]) => (
                          <Badge key={letter} variant="outline" className="font-mono font-normal">
                            {letter}: {pct}%
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">{t('fiscal.printers.taxRatesEmpty')}</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                    <div>
                      <p className="text-muted-foreground text-xs">{t('fiscal.printers.protectedMemoryFree')}</p>
                      <p>{printer.protectedMemoryFree !== null ? printer.protectedMemoryFree.toLocaleString('pl-PL') : '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('fiscal.printers.queueDepth')}</p>
                      <p>{printer.queueDepth !== null ? printer.queueDepth.toLocaleString('pl-PL') : '—'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs">{t('fiscal.printers.lastDailyReport')}</p>
                      <p>{printer.lastDailyReport ?? t('fiscal.printers.lastDailyReportNever')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('fiscal.printers.agentVersion')}</p>
                      <p>{printer.agentVersion ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('fiscal.printers.lastHeartbeat')}</p>
                      <p>{printer.lastHeartbeatAt ? formatRelativeTime(printer.lastHeartbeatAt) : t('fiscal.printers.lastHeartbeatNever')}</p>
                    </div>
                  </div>

                  <div className="border-t pt-2 text-xs">
                    <span className="text-muted-foreground">{t('fiscal.printers.dailyReportModeLabel')}:</span>{' '}
                    {t(`fiscal.printers.dailyReportMode.${printer.dailyReportMode}`)} · {printer.dailyReportTime}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add printer */}
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!addSubmitting) setAddOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.printers.addTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="printer-code">{t('fiscal.printers.codeLabel')}</Label>
              <Input
                id="printer-code"
                value={addForm.code}
                onChange={(e) => setAddForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))}
                placeholder="kasa-1"
                aria-invalid={codeError ? true : undefined}
              />
              {codeError ? (
                <p className="text-destructive text-xs">{codeError}</p>
              ) : (
                <p className="text-muted-foreground text-xs">{t('fiscal.printers.codeHint')}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="printer-name">{t('fiscal.printers.nameLabel')}</Label>
              <Input
                id="printer-name"
                value={addForm.name}
                onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t('fiscal.printers.namePlaceholder')}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t('fiscal.printers.dailyReportModeLabel')}</Label>
                <Select
                  value={addForm.dailyReportMode}
                  onValueChange={(v) => setAddForm((f) => ({ ...f, dailyReportMode: v as 'central' | 'agent' }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAILY_REPORT_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {t(`fiscal.printers.dailyReportMode.${mode}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="printer-time">{t('fiscal.printers.dailyReportTimeLabel')}</Label>
                <Input
                  id="printer-time"
                  type="time"
                  value={addForm.dailyReportTime}
                  onChange={(e) => setAddForm((f) => ({ ...f, dailyReportTime: e.target.value }))}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              {t(`fiscal.printers.dailyReportMode.${addForm.dailyReportMode}Hint`)}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleAddSubmit} disabled={addSubmitting || !addForm.code || !addForm.name.trim()}>
              {addSubmitting && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.printers.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit printer */}
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open && !editSubmitting) setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fiscal.printers.editTitle', { code: editing?.code ?? '' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="edit-printer-name">{t('fiscal.printers.nameLabel')}</Label>
              <Input
                id="edit-printer-name"
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{t('fiscal.printers.dailyReportModeLabel')}</Label>
                <Select
                  value={editForm.dailyReportMode}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, dailyReportMode: v as 'central' | 'agent' }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAILY_REPORT_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {t(`fiscal.printers.dailyReportMode.${mode}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-printer-time">{t('fiscal.printers.dailyReportTimeLabel')}</Label>
                <Input
                  id="edit-printer-time"
                  type="time"
                  value={editForm.dailyReportTime}
                  onChange={(e) => setEditForm((f) => ({ ...f, dailyReportTime: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-printer-active">{t('fiscal.printers.activeLabel')}</Label>
              <Switch
                id="edit-printer-active"
                checked={editForm.active}
                onCheckedChange={(checked) => setEditForm((f) => ({ ...f, active: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={editSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditSubmit} disabled={editSubmitting || !editForm.name.trim()}>
              {editSubmitting && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.printers.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate token confirm */}
      <AlertDialog
        open={regenTarget !== null}
        onOpenChange={(open) => {
          if (!open && !regenerating) setRegenTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fiscal.printers.regenerateConfirmTitle', { code: regenTarget?.code ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.printers.regenerateConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={regenerating} onClick={handleRegenerate}>
              {regenerating && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.printers.regenerateToken')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fiscal.printers.deleteConfirmTitle', { code: deleteTarget?.code ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.printers.deleteConfirmMessage')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} variant="destructive" onClick={handleDelete}>
              {deleting && <Loader2 className="size-4 animate-spin" />}
              {t('fiscal.printers.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Token reveal (create + regenerate share this) */}
      <AlertDialog open={reveal !== null} onOpenChange={(open) => !open && setReveal(null)}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('fiscal.printers.tokenRevealTitle', { code: reveal?.code ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('fiscal.printers.tokenRevealDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          {reveal && (
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5">
                <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-700 dark:text-amber-400">{t('fiscal.printers.tokenRevealWarning')}</p>
              </div>
              <div className="flex items-center gap-2">
                <code className="bg-muted flex-1 overflow-x-auto rounded px-2 py-1.5 font-mono text-xs">{reveal.token}</code>
                <Button variant="outline" size="sm" onClick={() => copyToClipboard(reveal.token, 'fiscal.printers.tokenCopied')}>
                  <Copy className="size-4" />
                  {t('fiscal.printers.copyToken')}
                </Button>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium">{t('fiscal.printers.dockerSnippetTitle')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(buildDockerSnippet(reveal.code, reveal.token), 'fiscal.printers.snippetCopied')}
                  >
                    <Copy className="size-4" />
                    {t('fiscal.printers.copySnippet')}
                  </Button>
                </div>
                <pre className="bg-muted overflow-x-auto rounded-md p-3 text-xs">
                  <code>{buildDockerSnippet(reveal.code, reveal.token)}</code>
                </pre>
                <p className="text-muted-foreground mt-1 text-xs">{t('fiscal.printers.dockerSnippetHint')}</p>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setReveal(null)}>{t('fiscal.printers.close')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
